/* Simple client-side export utilities for CSV, Excel, and PDF (print) */

export interface ExportExpenseRow {
  id: string;
  amount: number;
  currency: string;
  description: string;
  expense_date: string; // ISO string
  merchant_name?: string;
  status: string;
  category?: { id: string; name: string } | null;
  user?: { id: string; name?: string; email?: string } | null;
}

function formatDate(d: string): string {
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString();
  } catch {
    return d;
  }
}

function escapeCSV(val: any): string {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function buildRows(expenses: ExportExpenseRow[]) {
  return expenses.map((e) => ({
    Date: formatDate(e.expense_date),
    Description: e.description || '',
    Category: e.category?.name || '',
    Merchant: e.merchant_name || '',
    Amount: e.amount,
    Currency: e.currency,
    Status: e.status,
    User: e.user?.email || '',
  }));
}

export function exportExpensesToCSV(expenses: ExportExpenseRow[], filename = 'expenses.csv') {
  if (!expenses?.length) return;
  const rows = buildRows(expenses);
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(',')].concat(
    rows.map((r) => headers.map((h) => escapeCSV((r as any)[h])).join(','))
  );
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportExpensesToExcel(expenses: ExportExpenseRow[], filename = 'expenses.xls') {
  if (!expenses?.length) return;
  const rows = buildRows(expenses);
  const headers = Object.keys(rows[0]);
  const headerHtml = `<tr>${headers.map((h) => `<th style="text-align:left;border:1px solid #ddd;padding:4px;">${h}</th>`).join('')}</tr>`;
  const bodyHtml = rows
    .map((r) =>
      `<tr>${headers
        .map((h) => `<td style="border:1px solid #eee;padding:4px;">${(r as any)[h] ?? ''}</td>`)
        .join('')}</tr>`
    )
    .join('');
  const table = `<table>${headerHtml}${bodyHtml}</table>`;
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>${table}</body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportExpensesToPDF(expenses: ExportExpenseRow[], filename = 'expenses.pdf') {
  if (!expenses?.length) return;
  const rows = buildRows(expenses);
  const headers = Object.keys(rows[0]);
  const tableStyle = 'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:12px;';
  const thStyle = 'text-align:left;border:1px solid #ddd;padding:6px;background:#f0f4f8;';
  const tdStyle = 'border:1px solid #eee;padding:6px;';
  const headerHtml = `<tr>${headers.map((h) => `<th style="${thStyle}">${h}</th>`).join('')}</tr>`;
  const bodyHtml = rows
    .map((r) => `<tr>${headers.map((h) => `<td style="${tdStyle}">${(r as any)[h] ?? ''}</td>`).join('')}</tr>`)
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
 <meta charset="UTF-8" />
 <title>Expenses</title>
 <style>
  @media print { @page { size: A4 landscape; margin: 12mm; } }
  body { margin: 0; padding: 12px; }
  h1 { font-family: Arial, sans-serif; font-size: 16px; margin: 0 0 12px; }
 </style>
</head>
<body>
 <h1>Expenses Export</h1>
 <table style="${tableStyle}">${headerHtml}${bodyHtml}</table>
 <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
</body>
</html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url);
  // Fallback: if pop-up blocked, trigger download of HTML which user can print to PDF
  if (!w) {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expenses-print.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
